'use client';

import type { EraProduct, UserRole } from './auth.types';
import { shake } from 'radash';
import { useState } from 'react';
import { Env } from '@/libs/Env';
import { createClient } from '@/libs/supabaseSSR/client';

export function useAuthActions(query: {
  product: EraProduct;
  role?: UserRole;
  redirectTo?: string;
  referralCode?: string;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const supabase = createClient();

  const qs = new URLSearchParams(shake(query)).toString();

  async function signInWithOAuth(provider: 'github' | 'google') {
    try {
      setError(null);
      setLoading(true);

      // load coookies here and use redirecTo.
      const originalRedirectTo = `${window.location.origin}/callback?${qs}`;

      const invitedCustomInterviewsTemplateId = document.cookie
        .split('; ')
        .find(row => row.startsWith('invitedCustomInterviewsTemplateId='))
        ?.split('=')[1];
      console.info('invitedCustomInterviewsTemplateId=', invitedCustomInterviewsTemplateId);

      // if the user has a custom interview template id, we need to redirect to the custom interview template
      const redirectToPath = invitedCustomInterviewsTemplateId
        ? `/invite/custom-interviews-templates/${invitedCustomInterviewsTemplateId}?utm_source=email&utm_medium=campaign&utm_campaign=ai_tutor&useThankYouPage=true`
        : originalRedirectTo;

      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: redirectToPath,
        },
      });

      if (error) {
        throw error;
      }
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : 'An error occurred';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  async function signInWithEmail(email: string) {
    try {
      setError(null);
      setLoading(true);

      // const redirectTo = role === 'recruiter' ? '/admin/profiles' : '/setup';

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          // set this to false if you do not want the user to be automatically signed up
          shouldCreateUser: true,
          emailRedirectTo: `${window.location.origin}/callback?${qs}`,
        },
      });

      if (error) {
        throw error;
      }
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : 'An error occurred';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  async function signInWithHandshake() {
    try {
      setError(null);
      setLoading(true);

      const { data, error } = await supabase.auth.signInWithSSO({
        domain: Env.NEXT_PUBLIC_HANDSHAKE_PROVIDER_DOMAIN,
        options: {
          redirectTo: `${window.location.origin}/callback?${qs}`,
        },
      });

      if (data?.url) {
        // redirect the user to the identity provider's authentication flow
        window.location.href = `${Env.NEXT_PUBLIC_HANDSHAKE_SSO_AUTH_URL}${data.url}`;
      }

      if (error) {
        throw error;
      }
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : 'An error occurred';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  return {
    signInWithGitHub: () => signInWithOAuth('github'),
    signInWithGoogle: () => signInWithOAuth('google'),
    signInWithHandshake: () => signInWithHandshake(),
    signInWithEmail: (email: string) => signInWithEmail(email),
    loading,
    error,
  };
}
